import axios from "axios";
import withReactContent from "sweetalert2-react-content";
import Swal from "sweetalert2";

// Create axios instance
const httpClient = axios.create({
  baseURL: process.env.REACT_APP_API_URL,
  timeout: 15000,
  headers: {
    "Content-Type": "application/json",
  },
});

// Attach token automatically
httpClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem("authToken");

    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    const pageName = window.location.pathname.split("/").filter(Boolean).pop();

    config.headers["X-Page-URL"] = pageName || "";
    return config;
  },
  (error) => Promise.reject(error)
);

// Single session timeout handler
let sessionPopupShown = false;

const showSessionTimeout = () => {
  if (sessionPopupShown) return;

  sessionPopupShown = true;

  localStorage.removeItem("authToken");

  const MySwal = withReactContent(Swal);

  MySwal.fire({
    html: `<div class="alert alert-primary">
            <strong>Session</strong> expired. Please login again.
           </div>`,
    confirmButtonColor: "#c21607",
    confirmButtonText: "OK",
    allowOutsideClick: false,
    allowEscapeKey: false,
  }).then(() => {
    sessionPopupShown = false;
    window.location.replace("/login");
  });

  return new Promise(() => {});
};

// Centralized error handler
const handleError = (error) => {
  const response = error?.response;

  if (!response) {
    return {
      status: false,
      code: 0,
      message: "Network error. Please check your connection.",
      data: null,
    };
  }

  // 401 → token expired
  if (response.status === 401) {
    showSessionTimeout();
    return;
  }

  // 403 → permission denied
  if (response.status === 403 && window.location.pathname != "/login") {
    Swal.fire({
      icon: "error",
      title: "Access Denied",
      text: response?.data?.message || "Permission denied",
      confirmButtonColor: "#c21607",
      allowOutsideClick: false,
      allowEscapeKey: false,
      backdrop: "rgba(0,0,0,1)",
    }).then((result) => {
      if (result.isConfirmed) {
        window.location.href = window.location.origin;
      }
    });

    return new Promise(() => {});
  }

  return {
    status: false,
    code: response.status || 500,
    message: response?.data?.message || error.message || "Something went wrong",
    data: response?.data || null,
  };
};

// Standard response formatter
const handleResponse = (response) => {
  return {
    status: true,
    code: response.status,
    message: response?.data?.message || "Success",
    data: response?.data || null,
  };
};

// Unified API caller
const apiRequestCall = async (method, url, options = {}) => {
  try {
    const response = await httpClient({
      method,
      url,
      ...options,
    });

    return handleResponse(response);
  } catch (error) {
    return handleError(error);
  }
};

export default apiRequestCall;
